import { Input, InputProps, Textarea, TextareaProps } from '@headlessui/react';
import clsx from 'clsx';
import { type ReactNode } from 'react';

import { Field } from './field';
import { Hint } from './hint';
import { Label } from './label';

export type TextFieldProps = (InputProps | TextareaProps) & {
	ref?: any;
	type?: string;
	min?: string | number;
	max?: string | number;
	label?: ReactNode;
	hint?: ReactNode;
	size?: 'sm' | 'md' | 'lg';
	icon?: ReactNode;
	action?: ReactNode;
	miltiline?: boolean;
};

export const TextFiled = ({
	label,
	hint,
	size = 'md',
	icon,
	action,
	miltiline,
	className,
	...props
}: TextFieldProps) => {
	const Component = miltiline ? Textarea : Input;

	className = clsx(
		'[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
		{
			'py-3 text-sm': size === 'sm',
			'py-3 text-base': size === 'md',
			'py-3 text-base/7': size === 'lg',
			// states
			'cursor-not-allowed opacity-50': props.disabled,
			'cursor-not-allowed': props.readOnly,
			// 'border-red': props.invalid,
		},
		'flex w-full',
		'text-black',
		'placeholder:text-gray-3 autofill:bg-white data-[focus]:outline-none',
		className,
	);

	return (
		<Field>
			{label && (
				<div className="mb-2">
					<Label action={action}>{label}</Label>
				</div>
			)}

			<div
				className={clsx(
					'flex items-center gap-2 rounded-lg bg-white px-4 outline outline-1 -outline-offset-1 outline-gray-4 has-[autofill]:bg-white has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-gray-3',
					{
						'!outline-red': props.invalid,
					},
				)}
			>
				{icon && (
					<div className="-ml-1 shrink-0 select-none text-base text-gray-3">
						{icon}
					</div>
				)}
				<Component className={className} {...props} />
			</div>

			{hint && (
				<div className="mt-1">
					<Hint invalid={props.invalid}>{hint}</Hint>
				</div>
			)}
		</Field>
	);
};
